import {
  AUTHENTICATED_SHELL_PERMISSION,
  MEMBER_SHELL_PERMISSION,
  type NavigationPermissionRequirement,
} from '@/types/navigation-permissions';
import type { PermissionFeature } from '@/utils/permissions';

const list = (feature: PermissionFeature): NavigationPermissionRequirement => ({
  feature,
  action: 'list',
});

const read = (feature: PermissionFeature): NavigationPermissionRequirement => ({
  feature,
  action: 'read',
});

const create = (feature: PermissionFeature): NavigationPermissionRequirement => ({
  feature,
  action: 'create',
});

const update = (feature: PermissionFeature): NavigationPermissionRequirement => ({
  feature,
  action: 'update',
});

/**
 * Early Dashboard lists program clients so nutrition plans can be added.
 * Cohort analytics (`user_analytics`) stay closed for Nutritionist; meal-plan
 * create is the nutrition workflow that needs this screen.
 */
export const canAccessEarlyDashboard = (
  hasPermission: (feature: PermissionFeature, action: 'list' | 'create') => boolean
): boolean =>
  hasPermission('user_analytics', 'list') || hasPermission('nutrition_meal_plans', 'create');

/**
 * Authoritative map of navigation item ids → backend permission requirements.
 * Feature keys match the Django CRUD matrix (`show_crud_matrix`).
 */
export const NAVIGATION_ITEM_PERMISSIONS: Record<string, NavigationPermissionRequirement> = {
  // Workspace
  home: list('user_visits'),
  hub: list('user_visits'),
  business: list('user_program_enrollments'),
  consultation: list('consultations'),
  'app-dashboard': list('user_analytics'),
  appointments: list('appointments'),
  'page-recordings': list('page_recordings'),
  tickets: list('workflow_client_tickets'),
  notifications: list('user_notifications'),

  // Member preview (staff viewing member surfaces)
  'm-home': read('user_profiles'),
  'm-membership': read('user_profiles'),
  'm-health': read('user_profiles'),
  'm-reports': read('external_reports'),
  'm-wearables': list('wearable_connections'),
  'm-daily-log': list('daily_intake_logs'),
  'm-notifs': list('user_notifications'),
  'm-videos': list('consultation_videos'),
  'm-family': list('family_accounts'),
  'm-gift': list('marketplace_gifts'),
  'm-marketplace': list('marketplace_catalog'),
  'm-purchases': list('marketplace_catalog'),
  'm-benefits': list('marketplace_catalog'),
  'm-carts': list('marketplace_catalog'),
  'm-chat': read('platform_chat'),

  // Management
  people: list('user_management'),
  analytics: list('user_analytics'),
  nutrition: list('nutrition_meal_plans'),
  builder: list('thrive_iq_rules'),
  feedback: list('issue_reports'),
  ideas: list('product_ideas'),

  // Resources
  kb: list('knowledge_articles'),
  'ref-data': list('references_catalog'),
  'platform-chat': read('platform_chat'),
  pods: list('clinical_pods'),
  tags: list('client_tags'),
  groups: list('client_groups'),
  calendar: list('appointment_calendar'),
  chat: read('platform_chat'),
  'wearables-admin': list('wearable_connections'),
  'design-playground': list('ai_studio'),
  'marketplace-management': list('marketplace_item_config'),
  'service-queue': list('service_fulfillment'),
  'corporate-manage': update('corporate_accounts'),

  // Command palette / navigation-index page ids
  'consultation-dashboard': list('consultations'),
  backoffice: list('references_catalog'),
  users: list('user_management'),
  'business-dashboard': list('user_program_enrollments'),
  studio: list('ai_studio'),
  discovery: list('user_visits'),
  'backoffice-organization': list('reference_integrations'),
  'backoffice-clinical': list('references_biomarkers'),
  'backoffice-medical': list('references_medications'),
  'backoffice-lifestyle': list('references_lifestyles'),
  'backoffice-membership': list('references_memberships'),
  'client-visits': list('user_visits'),
  'client-appointments': list('appointments'),
  'client-clinical': list('clinical_notes'),
  'client-journal': list('user_journal'),
  'client-programs': list('user_program_enrollments'),
  'client-tests': list('user_partner_test_enrollments'),
  'client-questionnaires': list('assessment_responses'),
  'client-biomarkers': list('user_biomarker_analyses'),
  'client-reports': list('external_reports'),
  'client-pmx-reports': list('internal_reports'),
  'client-thrive-intelligence': list('thrive_iq_executions'),
  'client-sift': list('user_biomarker_analyses'),
  'client-journey': list('journey_activity'),
  'client-fitness-assessment': list('fitness_assessments'),
  'studio-playground': list('ai_studio_playground'),
  'studio-thrive-iq': list('thrive_iq_rules'),
  'studio-biomarker-ranges': list('references_biomarkers'),
  'studio-biomarkers': list('references_biomarkers'),
  'studio-clinical-templates': list('references_templates'),
  'thrive-iq-rules': list('thrive_iq_rules'),
  'thrive-iq-evaluation': list('thrive_iq_executions'),
  'backoffice-references': list('references_catalog'),
  'superclinic-catalog': list('catalog_registry'),
  'superclinic-registry': list('catalog_registry'),
  'clinical-biomarkers': list('references_biomarkers'),
  'clinical-biomarker-ranges': list('references_biomarkers'),
  'clinical-pathways': list('references_protocols'),
  'clinical-signs': list('references_signs'),
  'clinical-symptoms': list('references_symptoms'),
  'clinical-diagnoses': list('diagnosis_analysis'),
  'clinical-templates-backoffice': list('references_templates'),
  'backoffice-workflow': list('workflow_tasks'),
  'workflow-tasks': list('workflow_tasks'),
  'workflow-checklists': list('workflow_checklists'),
  'workflow-templates': list('program_templates'),
  'create-workflow-task': create('workflow_tasks'),
  'create-workflow-checklist': create('workflow_checklists'),

  // Quick link catalog keys
  'm-todo': read('user_profiles'),
  'm-profile': read('user_profiles'),
  'm-settings': read('user_profiles'),
  'm-support': read('user_profiles'),
  'm-ideas': list('product_ideas'),
  's-hub': list('user_visits'),
  's-business': list('user_program_enrollments'),
  's-appointments': list('appointments'),
  's-tickets': list('workflow_client_tickets'),
  's-people': list('user_management'),
  's-analytics': list('user_analytics'),
  's-nutrition': list('nutrition_meal_plans'),
  's-knowledge': list('knowledge_articles'),
  's-ideas': list('product_ideas'),
  'shared-preferences': AUTHENTICATED_SHELL_PERMISSION,

  'people-create': create('user_management'),
  'people-update': update('user_management'),
  'knowledge-create': create('knowledge_articles'),
  'knowledge-update': update('knowledge_articles'),
  'backoffice-program-create': create('references_programs'),
  'backoffice-addon-create': create('references_addons'),
  'backoffice-membership-create': create('references_memberships'),
  'workflow-tasks-update': update('workflow_tasks'),
  'workflow-teams-create': create('workflow_teams'),
  'workflow-checklists-update': update('workflow_checklists'),
  'prompt-management': list('prompt_management'),
  'prompt-create': create('prompt_management'),
  'prompt-update': update('prompt_management'),
  'feature-flags': list('feature_flags'),
};

export const getNavigationItemPermission = (
  id: string,
  fallback: NavigationPermissionRequirement = AUTHENTICATED_SHELL_PERMISSION
): NavigationPermissionRequirement => NAVIGATION_ITEM_PERMISSIONS[id] ?? fallback;

/** Route-level list/read permissions for top-level navigable paths. */
export const ROUTE_LIST_PERMISSIONS: Record<string, NavigationPermissionRequirement> = {
  '/': AUTHENTICATED_SHELL_PERMISSION,
  '/hub': list('user_visits'),
  '/home': list('user_visits'),
  '/appointments': list('appointments'),
  '/bookings-dashboard': list('consultations'),
  '/consultation-dashboard': list('consultations'),
  '/page-recordings': list('page_recordings'),
  '/backoffice': list('references_catalog'),
  '/users': list('user_management'),
  '/business-dashboard': list('user_program_enrollments'),
  '/studio': list('ai_studio'),
  '/builder': list('thrive_iq_rules'),
  '/discovery': list('user_visits'),
  '/dashboard': list('user_analytics'),
  '/knowledge-base': list('knowledge_articles'),
  '/feedback': list('issue_reports'),
  '/issue-reports': list('issue_reports'),
  '/ideas': list('product_ideas'),
  '/client-tickets': list('workflow_client_tickets'),
  '/notifications': list('user_notifications'),
  '/pods': list('clinical_pods'),
  '/tags': list('client_tags'),
  '/groups': list('client_groups'),
  '/calendar': list('appointment_calendar'),
  '/chat': read('platform_chat'),
  '/admin/wearables': list('wearable_connections'),
  '/design-playground': list('ai_studio'),
  '/whats-new': list('knowledge_articles'),
  '/platform-chat': read('platform_chat'),
  '/preferences': AUTHENTICATED_SHELL_PERMISSION,
  '/reports-compare': list('external_reports'),
  '/thrive-report': read('internal_reports'),
  '/nutrition': list('nutrition_meal_plans'),
  '/nutrition/planner': list('nutrition_meal_plans'),
  '/marketplace-management': list('marketplace_item_config'),
  '/service-queue': list('service_fulfillment'),
  '/app-dashboard': list('user_analytics'),
  '/corporates': list('corporate_accounts'),
  '/profiles': list('user_management'),
  '/families': list('user_management'),
  '/client': list('user_visits'),
  '/client-details': read('internal_reports'),
  '/member': read('user_profiles'),
  '/reference-data': list('references_catalog'),
  '/prompts': list('prompt_management'),
  '/corporate': list('corporate_accounts'),
};

/**
 * Paths already wrapped by a dedicated PermissionBasedRoute — skip list guard
 * to avoid duplicate checks.
 */
export const ROUTES_WITH_DEDICATED_PERMISSION_GUARD: string[] = [
  '/bookings-dashboard',
  '/consultation-dashboard',
  '/backoffice',
  '/users',
  '/profiles',
  '/corporates',
  '/families',
  '/feedback',
  '/ideas',
  '/dashboard',
  '/pods',
  '/tags',
  '/groups',
  '/prompts',
  '/marketplace-management',
  '/service-queue',
  '/admin/wearables',
  '/corporate',
];

const ROUTE_LIST_PREFIXES = Object.keys(ROUTE_LIST_PERMISSIONS).sort((a, b) => b.length - a.length);

/** Longest-prefix match for pathname → list/read permission requirement. */
export const resolveRouteListPermission = (
  pathname: string
): NavigationPermissionRequirement | null => {
  for (const routePath of ROUTE_LIST_PREFIXES) {
    if (pathname === routePath || pathname.startsWith(`${routePath}/`)) {
      return ROUTE_LIST_PERMISSIONS[routePath];
    }
  }
  return null;
};

/** Write routes guarded for READ_ONLY and other users without create/update. */
export const ROUTE_WRITE_PERMISSIONS: Record<string, NavigationPermissionRequirement> = {
  '/whats-new/add': create('knowledge_articles'),
  '/knowledge-base/add': create('knowledge_articles'),
  '/users/create': create('user_management'),
  '/profiles/create': create('user_profiles'),
  '/backoffice/create-task': create('workflow_tasks'),
  '/backoffice/create-checklist': create('workflow_checklists'),
  '/backoffice/create-program': create('references_programs'),
  '/backoffice/create-addon': create('references_addons'),
  '/backoffice/create-membership': create('references_memberships'),
  '/backoffice/create-team': create('workflow_teams'),
  '/nutrition/recipes/new': create('nutrition_recipes'),
  '/nutrition/meal-plans/new': create('nutrition_meal_plans'),
  '/nutrition/templates/new': create('nutrition_meal_plans'),
  '/nutrition/plan-builder': create('nutrition_meal_plans'),
  '/prompts/new': create('prompt_management'),
  '/prompts/:id/preview': read('prompt_management'),
};

export const resolveRouteWritePermission = (
  pathname: string
): NavigationPermissionRequirement | null => {
  if (ROUTE_WRITE_PERMISSIONS[pathname]) {
    return ROUTE_WRITE_PERMISSIONS[pathname];
  }
  if (/^\/prompts\/[^/]+\/preview$/.test(pathname)) {
    return read('prompt_management');
  }
  if (pathname.includes('/edit/') || pathname.endsWith('/edit')) {
    if (pathname.startsWith('/users') || pathname.startsWith('/profiles')) {
      return update('user_management');
    }
    if (pathname.startsWith('/knowledge-base')) {
      return update('knowledge_articles');
    }
    if (pathname.startsWith('/backoffice')) {
      return update('workflow_tasks');
    }
    if (pathname.startsWith('/whats-new')) {
      return update('knowledge_articles');
    }
    if (pathname.startsWith('/prompts')) {
      return update('prompt_management');
    }
    if (pathname.startsWith('/nutrition')) {
      return update('nutrition_meal_plans');
    }
  }
  return null;
};

export const STAFF_SHELL_PERMISSION = AUTHENTICATED_SHELL_PERMISSION;
export const MEMBER_ROUTE_PERMISSION = MEMBER_SHELL_PERMISSION;
export const CORPORATE_ROUTE_PERMISSION = list('corporate_accounts');
